import i18n from 'i18next';
import isEmpty from 'lodash/isEmpty';

import { CONTACT_SUPPORT_URL } from '../../config/constants';
import { getUserLanguage } from '../../i18n/helpers';
import { getUserReadableName } from '../../utils/getUserReadableName';
import type { Profile } from '../auth';

const APP_ID = 'nval56bo';
const API_BASE = 'https://api-iam.intercom.io';

class Intercom {
  private prevLang = getUserLanguage();

  private _settings: IntercomBootProps = {
    api_base: API_BASE,
    app_id: APP_ID,
    custom_launcher_selector: '.support-button',
  };

  constructor() {
    window.addEventListener('beforeunload', () => {
      if (!window.Intercom) {
        return;
      }

      window.Intercom('shutdown');
    });

    i18n.on('languageChanged', (rawLang) => {
      const lang = getUserLanguage(rawLang);

      if (this.prevLang !== lang) {
        this.prevLang = lang;
        window.Intercom?.('update', {
          language_override: lang.toLowerCase(),
        });
      }
    });
  }

  private _createUser(profile: Profile | null) {
    if (!profile?.intercomHash) {
      return {};
    }

    return {
      name: getUserReadableName(profile) ?? undefined,
      email: profile.email,
      user_id: profile.uniqueId,
      user_hash: profile.intercomHash,
      brand: 'crowncoins',
    };
  }

  private _loadIntercom(profile: Profile | null) {
    if (!window.Intercom) {
      return;
    }

    const settings: IntercomBootProps = {
      ...this._settings,
      ...this._createUser(profile),
      last_request_at: new Date().getTime() / 1000,
      language_override: getUserLanguage().toLowerCase(),
    };

    window.intercomSettings = settings;
    window.Intercom('shutdown');
    window.Intercom('boot', settings);
  }

  public identifyUser(profile: Profile | null) {
    if (!window.Intercom) {
      return;
    }
    if (isEmpty(profile)) {
      window.Intercom('shutdown');
      this._loadIntercom(null);
    } else {
      window.Intercom('shutdown');
      window.Intercom('update', {
        ...this._createUser(profile),
      });
    }
  }

  public init(profile: Profile | null) {
    if (!window.Intercom) {
      return;
    }
    console.log('Intercom shutdown');
    window.Intercom('shutdown');
    this._loadIntercom(profile);
  }

  public handleOpenSupport = () => {
    if (!window.Intercom) {
      window.open(CONTACT_SUPPORT_URL, '_blank');
      return;
    }
    setTimeout(() => {
      window.Intercom?.('show');
    }, 100);
  };
}
export const intercom = new Intercom();
